import { useCallback, useState } from "react";
import { useAuth } from "@/app/providers";

const { VITE_API_URL_GRAPHQL } = import.meta.env;

type UploadUserDocumentPayload = {
  file?: File | null;
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  parentId?: string;
  childId?: string;
  tenantId?: string;
  energyType?: string;
  documentType?: string;
};

/**
 * Hook to upload a user document via multipart/form-data.
 * Builds mutation string with literal values and appends file upload.
 * Uses Apollo's configured endpoint from environment variable.
 */
export const useUploadUserDocument = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // const client = useApolloClient();
  const { token } = useAuth();

  const uploadDocument = useCallback(
    async ({
      file,
      name,
      description,
      startDate,
      endDate,
      parentId,
      childId,
      tenantId,
      energyType,
      documentType,
    }: UploadUserDocumentPayload) => {
      setLoading(true);
      setError(null);

      try {
        // Escape quotes in string values to prevent GraphQL syntax errors
        const escapedName = (name || "").replace(/"/g, '\\"');
        const escapedDescription = (description || "").replace(/"/g, '\\"');

        // Build mutation with literal values (server expects this format, not variables)
        const query = `mutation uploadUserDocument{
          uploadUserDocument(
            name: "${escapedName}",
            description: "${escapedDescription}",
            startDate: "${startDate}",
            endDate: "${endDate}",
            parentId: "${parentId || ""}",
            childId: "${childId || ""}",
            tenantId: "${tenantId || ""}",
            energyType: "${energyType}"
            documentType: "${documentType}"
          ){
            success
            message
            documentId
          }
        }`;

        // Build multipart form data with mutation string and file
        const formData = new FormData();
        formData.append("query", query);
        if (file) {
          formData.append("file", file, file.name);
        }

        // Use the GraphQL endpoint from environment (same as GraphQLProvider)
        const uri = VITE_API_URL_GRAPHQL;

        // Build headers with authorization token (same as GraphQLProvider)
        const headers = {};
        if (token) {
          (headers as any).authorization = `Bearer ${token}`;
        }

        const response = await fetch(uri, {
          method: "POST",
          headers: headers,
          credentials: "include",
          body: formData,
        });

        const result = await response.json();

        if (result.errors) {
          throw new Error(result.errors[0]?.message || "Upload failed");
        }

        if (!result.data?.uploadUserDocument?.success) {
          const errorMessage = result.data?.uploadUserDocument?.message;
          throw new Error(
            Array.isArray(errorMessage)
              ? errorMessage[0]
              : errorMessage || "Upload failed",
          );
        }

        setData(result.data);
        setLoading(false);
        return result.data.uploadUserDocument;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : String(err) || "Upload failed",
        );
        setLoading(false);
        throw err;
      }
    },
    [token],
  );

  return { uploadDocument, data, loading, error };
};
